/**
 * Слой размеров на холсте — как их ставят на ремонтном чертеже: то, что можно измерить
 * рулеткой, а не оси стен. Каждая цифра — один раз:
 *
 *   • внутри каждой комнаты — длина каждой стены в чистоте, от угла до угла
 *     (`roomClearDimensions`): комната 2 × 3 м подписана 2,00 и 3,00, площадь — 6,00 м²;
 *   • снаружи — только габарит всего чертежа по наружным граням стен (`outerBounds`),
 *     серым: внутренние размеры плюс толщины стен.
 *
 * Стена, которая ещё не замыкает ни одной комнаты (её только чертят), подписана своей
 * длиной вдоль — иначе у неё не было бы размера вовсе.
 * Подпись, не помещающаяся на размере при текущем масштабе, прячется — линия остаётся.
 */
import {
	DimensionLine,
	buildDimensionSpans,
	dimensionLabelFits,
	outerBounds,
	roomClearDimensions,
	type Room,
	type WallGraph,
	type WallOutline,
} from "../../plan";
import { useEditorStore } from "@/widgets/editor";
import {
	add,
	dist,
	leftNormal,
	mul,
	norm,
	pointInPolygon,
	sub,
	type Pt,
} from "@/shared/lib/geometry";

interface Props {
	graph: WallGraph;
	/** Слой стен показан — иначе подписывать нечего */
	wallsVisible: boolean;
	rooms: Room[];
	/** Контуры стен — по ним габарит по наружным граням */
	outlines: Record<string, WallOutline>;
	mmPerPx: number;
}

/** Подпись размера — если помещается на нём при этом масштабе; иначе пустая. */
const fittingLabel = (from: Pt, to: Pt, mmPerPx: number) =>
	dimensionLabelFits(dist(from, to), mmPerPx) ? undefined : "";

export const DimensionsLayer2D = ({ graph, wallsVisible, rooms, outlines, mmPerPx }: Props) => {
	const showDimensions = useEditorStore((s) => s.showDimensions);
	if (!showDimensions || !wallsVisible) return null;

	// Стены, не задевающие ни одной комнаты, — подписываем их длину, пока комнаты нет
	const loose = buildDimensionSpans(graph).filter((span) => {
		const direction = norm(sub(span.to, span.from));
		const left = leftNormal(direction);
		const mid = { x: (span.from.x + span.to.x) / 2, y: (span.from.y + span.to.y) / 2 };
		const probe = span.thickness / 2 + 200;
		const inside = (n: Pt) =>
			rooms.some((room) => pointInPolygon(add(mid, mul(n, probe)), room.inner));
		return !inside(left) && !inside(mul(left, -1));
	});
	const clear = roomClearDimensions(rooms);
	const bounds = outerBounds(outlines);

	return (
		<>
			{/* Комнаты в чистоте — внутри, вдоль стен */}
			<g>
				{clear.map(({ from, to, normal }, k) => (
					<DimensionLine
						// Стороны комнат не переставляются — индекс как ключ здесь честный
						key={k}
						from={from}
						to={to}
						normal={normal}
						distance={22 * mmPerPx}
						mmPerPx={mmPerPx}
						minLength={1}
						label={fittingLabel(from, to, mmPerPx)}
					/>
				))}
			</g>
			{/* Отдельные стены без комнаты — их длина */}
			<g>
				{loose.map((span) => (
					<DimensionLine
						key={`dim-${span.id}`}
						from={span.from}
						to={span.to}
						normal={leftNormal(norm(sub(span.to, span.from)))}
						distance={span.thickness / 2 + 24 * mmPerPx}
						mmPerPx={mmPerPx}
						label={fittingLabel(span.from, span.to, mmPerPx)}
					/>
				))}
			</g>
			{bounds && (
				<g>
					<DimensionLine
						from={{ x: bounds.minX, y: bounds.maxY }}
						to={{ x: bounds.maxX, y: bounds.maxY }}
						normal={{ x: 0, y: 1 }}
						distance={40 * mmPerPx}
						mmPerPx={mmPerPx}
						muted
					/>
					<DimensionLine
						from={{ x: bounds.minX, y: bounds.minY }}
						to={{ x: bounds.minX, y: bounds.maxY }}
						normal={{ x: -1, y: 0 }}
						distance={40 * mmPerPx}
						mmPerPx={mmPerPx}
						muted
					/>
				</g>
			)}
		</>
	);
};
